import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import VariantModal from "./VariantModal";
import { convertGoogleDriveUrlForDisplay, getPlaceholderImage } from "../utils/imageUtils";

export default function ProductCard({ product, disabled=false, onAddToCart }){
  const navigate = useNavigate();
  const { name, images, discount, variants, _id, price, mrp } = product;
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [adding, setAdding] = useState(false);

  const hasVariants = variants && variants.length > 1;
  const variantCount = variants?.length || 0;
  const displayPrice = variants && variants.length > 0 ? variants[0].price : price;

  const handleAddClick = (e) => {
    e.stopPropagation(); // Prevent card click when clicking ADD button
    if (disabled) {
      alert("Unserviceable at your location. Please change address.");
      return;
    }
    
    if (!onAddToCart) {
      alert("Cart functionality not available. Please log in.");
      return;
    }

    // If product has variants, show modal
    if (hasVariants) {
      setShowVariantModal(true);
    } else {
      // Directly add to cart if no variants
      handleDirectAdd();
    }
  };

  const handleDirectAdd = async () => {
    try {
      setAdding(true);
      
      // Validate product ID before attempting to add to cart
      if (!_id) {
        alert('Error: Product ID is missing. Please refresh the page and try again.');
        setAdding(false);
        return;
      }
      
      const variant = variants && variants.length === 1 ? {
        sku: variants[0].sku,
        sizeLabel: variants[0].label,
        price: variants[0].price,
        mrp: variants[0].mrp
      } : null;
      
      await onAddToCart(_id, variant, 1);
      setAdding(false);
    } catch (error) {
      console.error('Error adding to cart:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to add to cart. Please try again.';
      alert(errorMsg);
      setAdding(false);
    }
  };

  const handleCardClick = (e) => {
    // Don't navigate if clicking on the ADD button or its container
    if (e.target.closest('.card-actions') || e.target.closest('.btn')) {
      return;
    }
    if (_id) {
      navigate(`/product/${_id}`);
    }
  };

  return (
    <>
      <div className={`product-card ${disabled ? "disabled" : ""}`}>
        <div className="img-wrap" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
          {discount > 0 && <div className="badge">{discount}% OFF</div>}
          <img 
            src={images && images.length > 0 ? convertGoogleDriveUrlForDisplay(typeof images[0] === 'string' ? images[0] : images[0].default || images[0]) : getPlaceholderImage(300, 300, 'No Image')} 
            alt={name} 
            onError={(e) => {
              // Prevent infinite retry loops
              if (e.target.dataset.retryCount) {
                const retryCount = parseInt(e.target.dataset.retryCount, 10);
                if (retryCount >= 2) {
                  // Stop retrying after 2 attempts, use placeholder
                  e.target.src = getPlaceholderImage(300, 300, 'No Image');
                  e.target.onerror = null; // Remove error handler to prevent further retries
                  return;
                }
                e.target.dataset.retryCount = (retryCount + 1).toString();
              } else {
                e.target.dataset.retryCount = '1';
              }

              const originalSrc = e.target.src;
              
              // Skip base64 and placeholder images
              if (originalSrc.startsWith('data:image/')) {
                e.target.src = getPlaceholderImage(300, 300, 'No Image');
                e.target.onerror = null;
                return;
              }

              // Skip problematic Google URLs
              if (originalSrc.includes('lh3.googleusercontent.com') || 
                  originalSrc.includes('drive.google.com/uc?export=download')) {
                e.target.src = getPlaceholderImage(300, 300, 'No Image');
                e.target.onerror = null;
                return;
              }

              // Try alternative Google Drive formats if first attempt fails
              if (originalSrc.includes('drive.google.com')) {
                // Extract file ID
                const fileIdMatch = originalSrc.match(/[?&]id=([a-zA-Z0-9_-]+)/) || 
                                  originalSrc.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) ||
                                  originalSrc.match(/\/d\/([a-zA-Z0-9_-]+)/);
                
                if (fileIdMatch && fileIdMatch[1]) {
                  const fileId = fileIdMatch[1];
                  
                  // Try thumbnail method (more reliable)
                  if (!originalSrc.includes('thumbnail')) {
                    e.target.src = `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
                    return;
                  }
                }
              }
              
              // Final fallback: use placeholder and stop retrying
              e.target.src = getPlaceholderImage(300, 300, 'No Image');
              e.target.onerror = null; // Remove error handler to prevent further retries
            }}
          />
        </div>
        <div className="card-body" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
          <h3 style={{ cursor: 'pointer' }}>{name}</h3>
          <div className="price-row">
            <span className="price">₹{displayPrice}</span>
            {mrp && mrp > displayPrice && (
              <span className="mrp">₹{mrp}</span>
            )}
          </div>
          {variants && variants.length > 0 && variants[0].label && (
            <div className="variant-info">{variants[0].label}</div>
          )}
          <div className="card-actions" onClick={(e) => e.stopPropagation()}>
            <button 
              className="btn primary" 
              onClick={handleAddClick} 
              disabled={disabled || adding}
            >
              {adding ? 'Adding...' : 'ADD'}
            </button>
            {hasVariants && (
              <span className="variant-count-text">{variantCount} options</span>
            )}
          </div>
          {disabled && <div className="unserv">Unserviceable at your location</div>}
        </div>
      </div>

      {showVariantModal && (
        <VariantModal
          product={product}
          isOpen={showVariantModal}
          onClose={() => setShowVariantModal(false)}
          onAddToCart={onAddToCart}
        />
      )}
    </>
  );
}
